// src/components/settings/catalog/CatalogBulkActions.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 4.
//
// The bulk bar ("N selected · Move to folder · Add to folder · Change prices… · Export selected ·
// Archive · Clear") and its dialogs. Every dialog hands the parent ONE startCatalogBulk input
// ({ action, percent?, folderId?, confirm? }); the parent adds the selection and runs the job.
//
// Over 500 items — or whenever the number isn't known exactly ("1,000+") — the dialog shows the
// orange "Type CHANGE to confirm" box and keeps its button off until the box says exactly CHANGE.
// The SERVER is the real guard: if it counts more than the screen did and refuses, the dialog
// reads its count from the refusal and shows the box then.
import React, { useMemo, useState } from 'react';
import { FolderInput, FolderPlus, Percent, Download, Archive, X } from 'lucide-react';
import {
    parsePercent, percentProblem, bulkPreviewPrice, needsTypedConfirm, isConfirmTyped, countText, bigChangeLine,
    confirmCountFromError, formatMoney,
} from '../../../utils/catalogUi';
import { friendlyError } from './catalogApi';
import CatalogFolderPicker from './CatalogFolderPicker';
import { BTN_GHOST, BTN_PRIMARY, BTN_DANGER, INPUT, Dialog, TypedConfirm, BIG_CHANGE_NOTE, ErrorLine } from './catalogUiParts';

const BAR_BTN = 'inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 disabled:opacity-50';

export function BulkBar({ label, archivedView, busy, onMove, onAdd, onPrices, onExport, onArchive, onClear }) {
    return (
        <div role="region" aria-label="Selected items" className="flex flex-wrap items-center gap-2 p-2 rounded-xl border border-[#5F4EC9]/30 bg-[#F4F2FD]">
            <span className="px-2 text-sm font-semibold text-[#5F4EC9]" aria-live="polite">{label}</span>
            <button type="button" className={BAR_BTN} disabled={busy} onClick={onMove}><FolderInput size={16} /> Move to folder</button>
            <button type="button" className={BAR_BTN} disabled={busy} onClick={onAdd}><FolderPlus size={16} /> Add to folder</button>
            <button type="button" className={BAR_BTN} disabled={busy} onClick={onPrices}><Percent size={16} /> Change prices…</button>
            <button type="button" className={BAR_BTN} disabled={busy} onClick={onExport}><Download size={16} /> Export selected</button>
            {!archivedView && <button type="button" className={`${BAR_BTN} text-red-700 hover:bg-red-50`} disabled={busy} onClick={onArchive}><Archive size={16} /> Archive</button>}
            <button type="button" className={BAR_BTN} disabled={busy} onClick={onClear}><X size={16} /> Clear</button>
        </div>
    );
}

// Shared by the three dialogs: the CHANGE box, the server's count, busy and error.
function useStart({ count, exact, onStart }) {
    const [typed, setTyped] = useState('');
    const [serverCount, setServerCount] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const shownCount = serverCount !== null ? serverCount : count;
    const shownExact = serverCount !== null ? true : exact;
    const mustType = serverCount !== null || needsTypedConfirm(count, exact);
    const ready = !mustType || isConfirmTyped(typed);
    const start = async (input) => {
        setBusy(true);
        setError('');
        try {
            await onStart({ ...input, ...(mustType ? { confirm: 'CHANGE' } : {}) });
        } catch (err) {
            const n = confirmCountFromError(err);
            if (n !== null) { setServerCount(n); setTyped(''); } else setError(friendlyError(err, "Couldn't start the change. Try again."));
            setBusy(false);
        }
    };
    return { typed, setTyped, busy, error, mustType, ready, start, shownCount, shownExact };
}

const itemsText = (count, exact) => (typeof count === 'number' ? `${countText(count, exact)} item${count === 1 && exact ? '' : 's'}` : 'all matching items');

// ── Change prices ───────────────────────────────────────────────────────────────────────
export function PriceDialog({ open, onClose, previewRows, allMatching, count, exact, onStart }) {
    const [mode, setMode] = useState('changePrice');
    const [text, setText] = useState('');
    const s = useStart({ count, exact, onStart });
    const p = parsePercent(text);
    const problem = text.trim() === '' ? null : percentProblem(mode, p);
    const valid = text.trim() !== '' && !problem;

    const preview = useMemo(() => {
        if (!valid) return [];
        return previewRows.map((r) => ({ r, res: bulkPreviewPrice(mode, p, { price: r.price, cost: r.cost }) }));
    }, [valid, previewRows, mode, p]);
    const tally = useMemo(() => {
        if (allMatching || !valid) return null;
        const t = { changed: 0, unchanged: 0, noCost: 0, noPrice: 0 };
        for (const { res } of preview) {
            if (res.result === 'changed') t.changed++;
            else if (res.result === 'unchanged') t.unchanged++;
            else if (res.reason === 'no-cost') t.noCost++;
            else t.noPrice++;
        }
        return t;
    }, [allMatching, valid, preview]);
    const SHOW = 8;

    return (
        <Dialog open={open} title="Change prices" onClose={onClose} busy={s.busy} wide
            footer={(
                <>
                    <button type="button" className={BTN_GHOST} disabled={s.busy} onClick={onClose}>Cancel</button>
                    <button type="button" className={BTN_PRIMARY} disabled={s.busy || !valid || !s.ready} onClick={() => s.start({ action: mode, percent: p })}>
                        {s.busy ? 'Starting…' : `Apply to ${itemsText(s.shownCount, s.shownExact)}`}
                    </button>
                </>
            )}>
            <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-gray-700 mb-1">What to do</legend>
                {[['changePrice', 'Change price by %'], ['setMarkup', 'Set markup to %']].map(([v, label]) => (
                    <label key={v} className={`flex items-center gap-2 min-h-[44px] px-3 rounded-lg border cursor-pointer text-sm ${mode === v ? 'border-[#5F4EC9] bg-[#5F4EC9]/10 text-[#5F4EC9] font-semibold' : 'border-gray-200 text-gray-700'}`}>
                        <input type="radio" name="cat-price-mode" className="w-4 h-4 accent-[#5F4EC9]" checked={mode === v} onChange={() => setMode(v)} />
                        {label}
                    </label>
                ))}
            </fieldset>
            <div>
                <label htmlFor="cat-percent" className="block text-sm font-medium text-gray-700 mb-1">{mode === 'changePrice' ? 'Change by' : 'Markup'}</label>
                <div className="relative w-40">
                    <input id="cat-percent" inputMode="decimal" className={`${INPUT} pr-8 text-right`} autoFocus value={text} placeholder={mode === 'changePrice' ? '10 or -3' : '30'}
                        onChange={(e) => setText(e.target.value)} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
                </div>
                {problem && <p className="text-sm text-red-700 mt-1">{problem}</p>}
            </div>
            <p className="text-xs text-gray-500">Use a minus sign to lower prices (−3). Rounded to the nearest cent. Each change goes into that item's price history. Estimates and invoices already written don't change.</p>

            {valid && (
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800">Preview</h3>
                    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                        {preview.slice(0, SHOW).map(({ r, res }) => (
                            <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm">
                                <span className="text-gray-800 truncate max-w-[60%]">{r.name || '(no name)'}</span>
                                <span className="tabular-nums text-gray-600">
                                    {res.result === 'changed' && <>{formatMoney(r.price)} → <span className="font-semibold text-gray-900">{formatMoney(res.price)}</span></>}
                                    {res.result === 'unchanged' && <>{formatMoney(r.price)} (no change)</>}
                                    {res.result === 'skipped' && res.reason === 'no-cost' && <span className="text-amber-700">(skipped: no cost)</span>}
                                    {res.result === 'skipped' && res.reason !== 'no-cost' && <span className="text-amber-700">(skipped: no price)</span>}
                                </span>
                            </li>
                        ))}
                    </ul>
                    {allMatching
                        ? <p className="text-sm text-gray-600">Showing the first few. All {itemsText(count, exact)} will be changed the same way.</p>
                        : preview.length > SHOW && <p className="text-sm text-gray-600">…and {(preview.length - SHOW).toLocaleString('en-US')} more.</p>}
                    {tally && (
                        <p className="text-sm text-gray-700">
                            {tally.changed.toLocaleString('en-US')} will change
                            {tally.noCost ? ` · ${tally.noCost} skipped (no cost)` : ''}
                            {tally.noPrice ? ` · ${tally.noPrice} skipped (no price)` : ''}
                            {tally.unchanged ? ` · ${tally.unchanged} already at that price` : ''}
                        </p>
                    )}
                </div>
            )}

            {s.mustType && valid && <TypedConfirm line={bigChangeLine(mode, s.shownCount, s.shownExact)} note={BIG_CHANGE_NOTE.price} value={s.typed} onChange={s.setTyped} />}
            <ErrorLine text={s.error} />
        </Dialog>
    );
}

// ── Move to folder / Add to folder ──────────────────────────────────────────────────────
export function FolderActionDialog({ open, action, onClose, tree, count, exact, onStart }) {
    const [folderId, setFolderId] = useState(null);
    const s = useStart({ count, exact, onStart });
    const moving = action === 'moveToFolder';
    const n = itemsText(s.shownCount, s.shownExact);
    return (
        <Dialog open={open} title={moving ? 'Move to folder' : 'Add to folder'} onClose={onClose} busy={s.busy}
            footer={(
                <>
                    <button type="button" className={BTN_GHOST} disabled={s.busy} onClick={onClose}>Cancel</button>
                    <button type="button" className={BTN_PRIMARY} disabled={s.busy || !folderId || !s.ready} onClick={() => s.start({ action, folderId })}>
                        {s.busy ? 'Starting…' : `${moving ? 'Move' : 'Add'} ${n}`}
                    </button>
                </>
            )}>
            <p className="text-sm text-gray-600">
                {moving ? 'Each item will be in this folder only — it’s taken out of any other folders.' : 'Each item keeps its other folders and is added to this one too.'}
            </p>
            <CatalogFolderPicker tree={tree} value={folderId} onChange={setFolderId} name="cat-bulk-folder" />
            {s.mustType && <TypedConfirm line={bigChangeLine(action, s.shownCount, s.shownExact)} note={BIG_CHANGE_NOTE.folder} value={s.typed} onChange={s.setTyped} />}
            <ErrorLine text={s.error} />
        </Dialog>
    );
}

// ── Archive ─────────────────────────────────────────────────────────────────────────────
export function ArchiveDialog({ open, onClose, count, exact, onStart }) {
    const s = useStart({ count, exact, onStart });
    const n = itemsText(s.shownCount, s.shownExact);
    return (
        <Dialog open={open} title={`Archive ${n}?`} onClose={onClose} busy={s.busy}
            footer={(
                <>
                    <button type="button" className={BTN_GHOST} disabled={s.busy} onClick={onClose}>Cancel</button>
                    <button type="button" className={BTN_DANGER} disabled={s.busy || !s.ready} onClick={() => s.start({ action: 'archive' })}>
                        {s.busy ? 'Starting…' : `Archive ${n}`}
                    </button>
                </>
            )}>
            <p className="text-sm text-gray-700">Archived items can't be added to new estimates or invoices, but old ones still show them. You can restore an item from Archived at any time.</p>
            {s.mustType && <TypedConfirm line={bigChangeLine('archive', s.shownCount, s.shownExact)} note={BIG_CHANGE_NOTE.archive} value={s.typed} onChange={s.setTyped} />}
            <ErrorLine text={s.error} />
        </Dialog>
    );
}
